import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  createManualUnderwriting,
  uploadFile,
} from "../../Store/quotationSlice";
import {
  Form,
  Input,
  Select,
  Button,
  Typography,
  Upload,
  Space,
  Card,
  Row,
  Col,
  Checkbox,
  Radio,
  message,
  Tag,
  Modal,
  Alert,
} from "antd";
import { UploadOutlined, PlusOutlined } from "@ant-design/icons";
import { useNavigate, useLocation } from "react-router-dom";
import PageBreadcrumb from "../Common/Breadcrumb";
import { fetchManualProducts, fetchSingleManualProducts } from "../../Store/quotationSlice";
import { getRenewalQuoteDetails, clearRejectedQuoteData } from "../../Store/renewalSlice";
import {uploadDocument} from "../../Store/quotationSlice";
import * as XLSX from 'xlsx';

const { Title } = Typography;
const { TextArea } = Input;

const MEMBER_TEMPLATE_URL =
  "https://eden-static-files.s3.ap-south-1.amazonaws.com/templates/Member-Information-Template.xlsx";

const NATURE_OF_BUSINESS_OPTIONS = [
  "Agriculture & Agribusiness",
  "Automotive & Transport Services",
  "Construction & Engineering",
  "Education & Training",
  "Energy & Utilities (Electricity, Water, Oil & Gas, Renewable Energy)",
  "Financial Services (Banking, Insurance, Investment, Fintech)",
  "Healthcare & Pharmaceuticals",
  "Hospitality & Tourism",
  "Manufacturing & Processing",
  "Media & Entertainment",
  "Mining & Natural Resources",
  "Non-Governmental Organizations (NGOs) & Non-Profits",
  "Professional Services (Law, Consulting, Accounting, HR, Marketing)",
  "Public Sector & Government Agencies",
  "Real Estate & Property Management",
  "Security Services (Private Security, Risk Consulting, Investigations)",
  "Sports & Fitness",
  "Technology & Telecommunications",
  "Transportation & Logistics",
  "Wholesale & Retail Trade",
];

const LOCATION_OPTIONS = [
  "Mombasa",
  "Kwale",
  "Kilifi",
  "Tana River",
  "Lamu",
  "Taita Taveta",
  "Garissa",
  "Wajir",
  "Mandera",
  "Marsabit",
  "Isiolo",
  "Meru",
  "Tharaka Nithi",
  "Embu",
  "Kitui",
  "Machakos",
  "Makueni",
  "Nyandarua",
  "Nyeri",
  "Kirinyaga",
  "Murang'a",
  "Kiambu",
  "Turkana",
  "West Pokot",
  "Samburu",
  "Trans Nzoia",
  "Uasin Gishu",
  "Elgeyo Marakwet",
  "Nandi",
  "Baringo",
  "Laikipia",
  "Nakuru",
  "Narok",
  "Kajiado",
  "Kericho",
  "Bomet",
  "Kakamega",
  "Vihiga",
  "Bungoma",
  "Busia",
  "Siaya",
  "Kisumu",
  "Homa Bay",
  "Migori",
  "Kisii",
  "Nyamira",
  "Nairobi City",
];

const normalizeDocumentPayload = (document, fallbackFileName) => {
  if (!document) return undefined;

  const normalized = {
    file_id: document.file_id || document.id,
    file_url: document.file_url || document.url,
    original_filename:
      document.original_filename || document.file_name || fallbackFileName,
    content_type: document.content_type,
    file_size: document.file_size,
    document_type: document.document_type,
    document_expiry_date: document.document_expiry_date,
    description: document.description,
  };

  Object.keys(normalized).forEach(
    (key) => normalized[key] === undefined && delete normalized[key]
  );

  return normalized;
};

const extractUploadResult = (apiResponse) => {
  const result = apiResponse?.data?.result;
  return Array.isArray(result) ? result?.[0] : result;
};

const PRODUCT_OPTIONS = ["SME Product Eden Care Medical", "U Care Premier Plus"];

const FUNDING_ARRANGEMENT_OPTIONS = ["Insured", "Fund Managed", "Hybrid"];

const AGGREGATION_LEVEL_OPTIONS = ["Shared Limit", "Non-Shared Limit"];

const SERVICE_PROVIDER_OPTIONS = ["Tier 1", "Tier 2", "Tier 3"];

const CATEGORY_OPTIONS = ["Executive", "Management", "Employee", "Contract", "Dependants/Families"];

const CUSTOM_CATEGORY_OPTION = "Custom Category";

// Top-level normalize function for header/field normalization
const normalize = str => (str || '').trim().toLowerCase();

const ManualQuoteRequest = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const loading = useSelector((state) => state.quotationSlice.loading);
  const fileUploadLoading = useSelector(
    (state) => state.quotationSlice.fileUploadLoading
  );
  const rejectedQuoteData = useSelector((state) => state.renewal.rejectedQuoteData);
  const [form] = Form.useForm();
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [customCategoryName, setCustomCategoryName] = useState("");
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [messageApi, messageContextHolder] = message.useMessage();
  const [products, setProducts] = useState([]);
  const [singleProduct, setSingleProduct] = useState(null);
  
  // Add state to track which product is selected
  const [selectedProductName, setSelectedProductName] = useState("");
  const [selectedProductId, setSelectedProductId] = useState(null);
  const [companySize, setCompanySize] = useState(null);
  
  // Member template preview and validation state
  const [memberPreviewModal, setMemberPreviewModal] = useState(false);
  const [memberTableData, setMemberTableData] = useState([]);
  const [memberTableHeaders, setMemberTableHeaders] = useState([]);

  // Allowed headers and categories
  const REQUIRED_HEADERS = [
    'First Name',
    'Last Name',
    'Date of Birth',
    'Gender',
    'Relation',
    'Benefit Tier',
  ];
  const ALLOWED_CATEGORIES = [
    'Executive',
    'Management',
    'Employee',
    'Contract_Worker',
    'Dependents',
  ];

  // Helper function to render appropriate benefit input based on product
  const renderBenefitInput = (category, benefitName, options) => {
    // Check if the product has predefined options for this benefit
    const benefitType = benefitName.split('_')[0]; // e.g., "inpatient_limit" -> "inpatient"
    const hasPredefinedOptions = options[benefitType] && options[benefitType].length > 0;
    
    if (hasPredefinedOptions) {
      return (
        <Select
          placeholder="Select limit"
          options={options[benefitType]}
          showSearch
          optionFilterProp="label"
          filterOption={(input, option) =>
            (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
          }
          style={{ width: '100%' }}
          onChange={(value) => {
            form.setFieldsValue({
              benefits_limits_and_aggregations: {
                [category]: {
                  [benefitName]: parseInt(value).toLocaleString("US"),
                },
              },
            });
          }}
        />
      );
    }
    
    // Default input field for benefits without predefined options
    return (
      <Input
        prefix="KES"
        placeholder="Enter amount"
        onChange={(e) => {
          const value = e.target.value.replace(/,/g, "");
          if (!isNaN(value)) {
            form.setFieldsValue({
              benefits_limits_and_aggregations: {
                [category]: {
                  [benefitName]: Number(value).toLocaleString("US"),
                },
              },
            });
          }
        }}
      />
    );
  };

  // Map deal data from location state if available
  useEffect(() => {
    if (location.state?.prospect) {
      console.log("location.state?.prospect", location.state?.prospect);
      const dealData = location.state?.prospect;

      form.setFieldsValue({
        business_name: dealData.business_name,
        business_email: dealData.email,
        business_phone: dealData.contact,
        client_budget: parseFloat(dealData.deal_value).toLocaleString("US"),
        size_of_company: dealData.size_of_company.includes("-") ? dealData.size_of_company.split("-")[1] : dealData.size_of_company,
        registration_number: "", // No equivalent field in the provided object
        nature_of_business: NATURE_OF_BUSINESS_OPTIONS.find(location => location.toLowerCase()?.includes(dealData?.nature_of_business?.toLowerCase())) || "",
        notes: `
1. Deal Owner: ${dealData.owner}
2. Channel: ${dealData.source}`
      });
    }
  }, [location.state, form]);
  
  // State to track if we're handling a renewal
  const [isRenewal, setIsRenewal] = useState(false);
  const [renewalAccountId, setRenewalAccountId] = useState(null);

  // Parse URL query parameters to check for renewal
  useEffect(() => {
    const queryParams = new URLSearchParams(location.search);
    const accountId = queryParams.get('account_id');
    const quoteType = queryParams.get('quote_type');
    
    if (accountId && quoteType === 'RENEWAL') {
      setIsRenewal(true);
      setRenewalAccountId(accountId);
      
      // Check if we have the rejected quote data from a previous rejection
      if (rejectedQuoteData) {
        populateFormWithRejectedData(rejectedQuoteData);
      }
    }
  }, [ location.search, form, rejectedQuoteData]);

  // Function to populate form with rejected quote data
  const populateFormWithRejectedData = (data) => {
    // Extract customer info depending on response structure
    const customerInfo = data.customer_info || data.result?.customer_info || {};
    const quoteDetails = data.quote_details || data.result?.quote_details || data;
    
    // Map fields to form from rejected quote data
    form.setFieldsValue({
      business_name: customerInfo.business_name || "",
      business_email: customerInfo.business_email || "",
      registration_number: customerInfo.registration_number || "",
      pin_number: customerInfo.pin_number || "",
      nature_of_business: customerInfo.business_nature || customerInfo.nature_of_business || "",
      location_of_business: customerInfo.business_location || customerInfo.location_of_business || "",
      size_of_company: customerInfo.size_of_company || "",
      location_of_majority_insured_members: customerInfo.location_of_majority_insured_members || "",
      cover_duration: quoteDetails.cover_duration || "",
    });
    
    // Set client budget from total_premium or premium if available
    if (quoteDetails.total_premium || quoteDetails.premium) {
      const premiumValue = quoteDetails.total_premium || quoteDetails.premium;
      form.setFieldsValue({
        client_budget: parseFloat(premiumValue).toLocaleString("US")
      });
    }
    
    // If we have product information, set the product
    if (quoteDetails.product_name || quoteDetails.product) {
      const productName = quoteDetails.product_name || quoteDetails.product;
      form.setFieldsValue({
        product: productName,
        funding_arrangement: quoteDetails.funding_arrangement || "Insured",
        aggregation_level: quoteDetails.aggregation_level || "Non-Shared Limit",
      });
      
      // Check if this is U Care Premier Plus (case insensitive)
      const isUCarePremierPlus = 
        productName && 
        (productName.toLowerCase().includes('u care premier plus') || 
         productName.toLowerCase().includes('ucare premier') ||
         productName.toLowerCase().includes('premier plus'));
      
      // If product is U Care Premier Plus, set the state accordingly
      if (isUCarePremierPlus) {
        setSelectedProductName(productName);
        console.log("Setting product name from rejected data:", productName);
      }
    }
    
    // If we have benefits information, set up the benefits sections
    if (quoteDetails.benefits || quoteDetails.benefit_limits) {
      const benefits = quoteDetails.benefits || quoteDetails.benefit_limits || [];
      
      // Set default categories for benefits - either use existing or default to Employee
      const categories = ['Employee']; // Default category
      setSelectedCategories(categories);
      
      // Map benefit limits if available
      try {
        // This is a simplified approach - you may need to adjust based on your data structure
        const inpatientBenefit = benefits.find(b => b.benefit_name?.toLowerCase() === 'inpatient');
        const outpatientBenefit = benefits.find(b => b.benefit_name?.toLowerCase() === 'outpatient');
        const dentalBenefit = benefits.find(b => b.benefit_name?.toLowerCase() === 'dental');
        const opticalBenefit = benefits.find(b => b.benefit_name?.toLowerCase() === 'optical');
        
        categories.forEach(category => {
          const formBenefits = {};
          
          if (inpatientBenefit) {
            formBenefits.inpatient_limit = parseInt(inpatientBenefit.benefit_limit).toLocaleString("US");
            formBenefits.inpatient_aggregation = inpatientBenefit.aggregation_level?.toLowerCase() === 'shared limit' ? 'shared' : 'non_shared';
          }
          
          if (outpatientBenefit) {
            formBenefits.outpatient_limit = parseInt(outpatientBenefit.benefit_limit).toLocaleString("US");
            formBenefits.outpatient_aggregation = outpatientBenefit.aggregation_level?.toLowerCase() === 'shared limit' ? 'shared' : 'non_shared';
          }
          
          if (dentalBenefit) {
            formBenefits.dental_limit = parseInt(dentalBenefit.benefit_limit).toLocaleString("US");
            formBenefits.dental_aggregation = dentalBenefit.aggregation_level?.toLowerCase() === 'shared limit' ? 'shared' : 'non_shared';
          }
          
          if (opticalBenefit) {
            formBenefits.optical_limit = parseInt(opticalBenefit.benefit_limit).toLocaleString("US");
            formBenefits.optical_aggregation = opticalBenefit.aggregation_level?.toLowerCase() === 'shared limit' ? 'shared' : 'non_shared';
          }
          
          if (Object.keys(formBenefits).length > 0) {
            form.setFieldsValue({
              benefits_limits_and_aggregations: {
                [category]: formBenefits
              }
            });
          }
        });
      } catch (error) {
        console.error("Error mapping benefits data:", error);
      }
    }
    
    // Add any relevant sales notes from previous quote
    if (quoteDetails.notes || quoteDetails.rejection_reason) {
      form.setFieldsValue({
        notes: `Previous quote notes: ${quoteDetails.notes || ''}\nRejection reason: ${quoteDetails.rejection_reason || ''}`
      });
    }
  };
  
  // Function to populate form with quote details
  const populateFormWithQuoteDetails = (response) => {
    const quoteDetails = response;
    const customerInfo = quoteDetails.customer_info || {};
    
    // Map fields to form - refer to the example JSON structure
    form.setFieldsValue({
      business_name: customerInfo.business_name || "",
      business_email: customerInfo.business_email || "",
      registration_number: customerInfo.registration_number || "",
      pin_number: customerInfo.pin_number || "",
      nature_of_business: customerInfo.business_nature || "",
      location_of_business: customerInfo.business_location || "",
      size_of_company: customerInfo.size_of_company || "",
    });
    
    // Set default categories for benefits
    setSelectedCategories(["Employee"]);
    
    // If we have total_premium, set as client budget
    if (quoteDetails.total_premium) {
      form.setFieldsValue({
        client_budget: parseFloat(quoteDetails.total_premium).toLocaleString("US")
      });
    }
  };

  // Clean up rejected quote data when component unmounts
  useEffect(() => {
    return () => {
      if (rejectedQuoteData) {
        dispatch(clearRejectedQuoteData());
      }
    };
  }, [ rejectedQuoteData]);

  // Add account_id field for debugging (optional)
  useEffect(() => {
    if (isRenewal && renewalAccountId) {
      // Add a hidden field to the form to store the account_id
      form.setFieldsValue({
        renewal_account_id: renewalAccountId,
        renewal_quote_type: "RENEWAL"
      });
    }
  }, [isRenewal, renewalAccountId, form]);

  const handleFileUpload = async (file, fieldName) => {
    try {
      const response = await dispatch(uploadFile(file)).unwrap();
      const fileData = extractUploadResult(response);
      const normalizedDoc = normalizeDocumentPayload(fileData, file?.name);

      if (!normalizedDoc?.file_url && !normalizedDoc?.file_id) {
        throw new Error(`Failed to upload ${fieldName}`);
      }
      
      form.setFieldsValue({
        [fieldName]: {
          ...normalizedDoc,
          // Keep legacy keys for existing UI bindings.
          url: normalizedDoc.file_url,
          file_name: normalizedDoc.original_filename,
          file: file // Keep the original file object
        },
      });
      return false; // Prevent default upload
    } catch (error) {
      console.error("File upload failed:", error);
      messageApi.error(`Failed to upload ${fieldName}`);
      return false;
    }
  };

  // Helper to convert Excel serial date to YYYY-MM-DD
  const excelSerialToDate = (serial) => {
    if (!serial || isNaN(Number(serial))) return serial;
    // Excel's epoch starts at 1900-01-01
    const excelEpoch = new Date(1899, 11, 30);
    const date = new Date(excelEpoch.getTime() + (Number(serial) * 24 * 60 * 60 * 1000));
    // Return as YYYY-MM-DD
    return date.toISOString().slice(0, 10);
  };

  // Store the uploaded file and parse only on preview
  const [memberTemplateFile, setMemberTemplateFile] = useState(null);
  const handleMemberTemplateUpload = async (info) => {
    const file = info.file;
    setMemberTemplateFile(file);
    
    try {
      const response = await dispatch(uploadFile(file)).unwrap();
      const fileData = extractUploadResult(response);
      const normalizedDoc = normalizeDocumentPayload(fileData, file?.name);

      if (!normalizedDoc?.file_url && !normalizedDoc?.file_id) {
        throw new Error('Failed to upload member information template');
      }
      
      form.setFieldsValue({
        member_information_template: {
          ...normalizedDoc,
          // Keep legacy keys for existing UI bindings.
          url: normalizedDoc.file_url,
          file_name: normalizedDoc.original_filename,
          file: file
        }
      });
    } catch (error) {
      console.error("Member template upload failed:", error);
      messageApi.error("Failed to upload member information template");
      return false;
    }
    
    // Parse file for preview only
    const reader = new FileReader();
    reader.onload = (e) => {
      const data = new Uint8Array(e.target.result);
      const workbook = XLSX.read(data, { type: 'array' });
      const sheetName = workbook.SheetNames[0];
      const worksheet = workbook.Sheets[sheetName];
      const json = XLSX.utils.sheet_to_json(worksheet, { defval: '' });
      const headers = XLSX.utils.sheet_to_json(worksheet, { header: 1 })[0] || [];
      
      // Build normalized rows for preview
      const normalizedRows = json.map(row => {
        const newRow = {};
        Object.keys(row).forEach(k => {
          newRow[normalize(k)] = row[k];
        });
        return newRow;
      });

      setMemberTableHeaders(headers);
      setMemberTableData(normalizedRows);
    };
    reader.readAsArrayBuffer(file);
    return false;
  };

  const uploadProps = {
    beforeUpload: (file, fileList) => {
      return false;
    },
    onChange: async (info, fieldName) => {
      if (fieldName === 'member_information_template' && info.file.status !== 'removed') {
        await handleMemberTemplateUpload(info);
      } else if (info.file.status !== "uploading") {
        await handleFileUpload(info.file, fieldName);
      }
    },
  };

  useEffect(() => {
    const fetch = async () => {
      const res = await dispatch(fetchManualProducts());
      const products = res?.payload?.data?.filter(a => a?.status === "Active");
      setProducts(products && products.length > 0 ? products : []);
    };
    if(products.length === 0){
      fetch();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSubmit = async (values) => {
    try {
      // Transform benefits data
      let benefits_limits_and_aggregation = [];
      if (values.benefits_limits_and_aggregations) {
        benefits_limits_and_aggregation = selectedCategories.map((category) => {
          const categoryData = values.benefits_limits_and_aggregations[category];
          return {
            scheme_category: category,
            copay: categoryData.copay ? parseInt(categoryData.copay.replace(/,/g, "")) : 0,
            service_provider_access: categoryData.service_provider_access || [],
            benefits: [
              {
                benefit_name: "Dental",
                benefit_limit: categoryData.dental_limit ? parseInt(categoryData.dental_limit.replace(/,/g, "")) : "0",
                aggregation_level: categoryData.dental_aggregation || "Non-Shared Limit",
              },
              {
                benefit_name: "Inpatient",
                benefit_limit: categoryData.inpatient_limit ? parseInt(categoryData.inpatient_limit.replace(/,/g, "")) : "0",
                aggregation_level: categoryData.inpatient_aggregation || "Shared Limit",
              },
              {
                benefit_name: "Optical",
                benefit_limit: categoryData.optical_limit ? parseInt(categoryData.optical_limit.replace(/,/g, "")) : "0",
                aggregation_level: categoryData.optical_aggregation || "Shared Limit",
              },
              {
                benefit_name: "Outpatient",
                benefit_limit: categoryData.outpatient_limit ? parseInt(categoryData.outpatient_limit.replace(/,/g, "")) : "0",
                aggregation_level: categoryData.outpatient_aggregation || "Non-Shared Limit",
              },
            ],
          };
        });
      }

      // Handle file uploads for claim_report and member_information_template
      let claimReportPayload = undefined;
      let memberTemplatePayload = undefined;

      // Claim Report
      if (
        values.claim_report &&
        values.claim_report.file &&
        !values.claim_report.url &&
        !values.claim_report.file_url &&
        !values.claim_report.file_id
      ) {
        // Not uploaded yet
        const uploadRes = await dispatch(uploadDocument(values.claim_report.file)).unwrap();
        const fileData = extractUploadResult(uploadRes);
        const normalizedDoc = normalizeDocumentPayload(
          fileData,
          values.claim_report.file?.name
        );
        if (!normalizedDoc?.file_url && !normalizedDoc?.file_id) {
          throw new Error('Failed to upload claim report');
        }
        claimReportPayload = normalizedDoc;
      } else if (
        values.claim_report &&
        (values.claim_report.url ||
          values.claim_report.file_url ||
          values.claim_report.file_id)
      ) {
        claimReportPayload = normalizeDocumentPayload(
          values.claim_report,
          values.claim_report.file?.name
        );
      }

      // Member Information Template
      if (
        values.member_information_template &&
        values.member_information_template.file &&
        !values.member_information_template.url &&
        !values.member_information_template.file_url &&
        !values.member_information_template.file_id
      ) {
        const uploadRes = await dispatch(uploadDocument(values.member_information_template.file)).unwrap();
        const fileData = extractUploadResult(uploadRes);
        const normalizedDoc = normalizeDocumentPayload(
          fileData,
          values.member_information_template.file?.name
        );
        if (!normalizedDoc?.file_url && !normalizedDoc?.file_id) {
          throw new Error('Failed to upload member information template');
        }
        memberTemplatePayload = normalizedDoc;
      } else if (
        values.member_information_template &&
        (values.member_information_template.url ||
          values.member_information_template.file_url ||
          values.member_information_template.file_id)
      ) {
        memberTemplatePayload = normalizeDocumentPayload(
          values.member_information_template,
          values.member_information_template.file?.name
        );
      }

      // Construct final payload
      const finalValues = {
        business_name: values.business_name,
        client_budget: values.client_budget ? parseInt(values.client_budget.replace(/,/g, "")) : 0,
        registration_number: values.registration_number,
        pin_number: values.pin_number,
        region: "Kenya",
        notes: values.notes,
        cover_duration: values.cover_duration,
        business_email: values.business_email,
        nature_of_business: values.nature_of_business,
        location_of_business: values.location_of_business,
        location_of_majority_insured_members: values.location_of_majority_insured_members,
        size_of_company: values.size_of_company?.toString(),
        funding_arrangement: values.funding_arrangement,
        product: values.product,
        product_id: selectedProductId,
        aggregation_level: values.aggregation_level,
        benefits_limits_and_aggregation,
        prior_bidding_rounds: values.prior_bidding_rounds || "",
        previous_lives_covered: values.previous_lives_covered || "",
        claims_experience: values.claims_experience || "",
        current_provider: values.current_provider || "",
        claim_report: claimReportPayload,
        member_information_template: memberTemplatePayload,
      };

      // Add renewal-specific fields if this is a renewal
      if (isRenewal && renewalAccountId) {
        finalValues.quote_type = "RENEWAL";
        finalValues.account_id = renewalAccountId;
      }

      // Remove any undefined values
      Object.keys(finalValues).forEach(
        (key) => finalValues[key] === undefined && delete finalValues[key]
      );

      const response = await dispatch(
        createManualUnderwriting(finalValues)
      ).unwrap();
      
      // Clear rejected quote data from Redux after successful submission
      if (rejectedQuoteData) {
        dispatch(clearRejectedQuoteData());
      }
      
      localStorage.setItem("quoteTab", "manual");
      messageApi.success("Quote request submitted successfully");
      form.resetFields();
      navigate("/quotes");
    } catch (error) {
      console.error("Failed to submit:", error);
      messageApi.error("Failed to submit quote request");
    }
  };

  const handleCategoryChange = (e) => {
    const value = e.target.value;
    
    if (value === CUSTOM_CATEGORY_OPTION) {
      setIsModalVisible(true);
      return;
    }
    
    if (selectedCategories.includes(value)) {
      setSelectedCategories(
        selectedCategories.filter((category) => category !== value)
      );
    } else {
      setSelectedCategories([...selectedCategories, value]);
    }
  };

  const handleAddCustomCategory = () => {
    setIsModalVisible(true);
  };

  const handleCustomCategorySubmit = () => {
    if (customCategoryName.trim()) {
      setSelectedCategories([...selectedCategories, customCategoryName.trim()]);
      setCustomCategoryName("");
      setIsModalVisible(false);
    }
  };

  const handleModalCancel = () => {
    setCustomCategoryName("");
    setIsModalVisible(false);
  };

  const [rateCardData, setRateCardData] = useState(null);
  const [benefitOptions, setBenefitOptions] = useState({
    inpatient: [],
    outpatient: [],
    dental: [],
    optical: []
  });

  // Add new function to fetch rate card data
  const fetchRateCardData = async (productId) => {
    try {
      const response = await fetch(`https://product-and-underwritting-config.vercel.app/api/product/rate-card?productId=${productId}`);
      const data = await response.json();
      setRateCardData(data.data);
      
      // Process rate card data to create dropdown options
      const options = {};

      // Dynamically process all benefits from rate card data
      Object.entries(data.data).forEach(([benefitType, benefitData]) => {
        if (Array.isArray(benefitData)) {
          options[benefitType.toLowerCase()] = benefitData.map(card => ({
            value: card.cover_limit,
            label: `KES ${parseInt(card.cover_limit).toLocaleString("US")}`
          }));
        }
      });

      setBenefitOptions(options);
      return data.data;
    } catch (error) {
      console.error("Error fetching rate card data:", error);
      messageApi.error("Failed to fetch rate card data");
      return null;
    }
  };

  const handleProductChange = async (value, option) => {
    // Set the selected product name for conditional rendering
    setSelectedProductName(value);
    
    // Extract product_id from option or find it in the products array
    let productId = null;
    if (option && option.key) {
      productId = option.key;
    } else {
      const selectedProduct = products.find(p => p.product_name === value);
      productId = selectedProduct?.product_id || null;
    }
    
    // Store the product ID
    setSelectedProductId(productId);
    
    const selectedProduct = products.find(p => p.product_name === value);

    form.setFieldsValue({
      product: value,
      aggregation_level: selectedProduct?.aggregation_level_code || "Non-Shared Limit"
    });
    
    // Fetch rate card data for the selected product
    const rateData = await fetchRateCardData(productId);
    
    if (rateData) {
      // Set default categories
      setSelectedCategories([ 'Executive',
        'Management']);
      
      // Process rate card data and set form values
      const processRateCardData = (category) => {
        const benefits = {};
        
        // Dynamically process all benefits from rate card data
        Object.entries(rateData).forEach(([benefitType, benefitData]) => {
          if (Array.isArray(benefitData) && benefitData.length > 0) {
            const firstRateCard = benefitData[0];
            const benefitKey = benefitType.toLowerCase();
            
            // Set the limit
            benefits[`${benefitKey}_limit`] = parseInt(firstRateCard.cover_limit).toLocaleString("US");
            // Set default aggregation to shared
            benefits[`${benefitKey}_aggregation`] = "shared";
          }
        });
        
        return benefits;
      };
      
      // Set form values for each category
      for (let category of CATEGORY_OPTIONS) {
        form.setFieldsValue({
          benefits_limits_and_aggregations: {
            [category]: processRateCardData(category)
          }
        });
      }
    } else {
      // Keep the original API call for other products
      dispatch(fetchSingleManualProducts(selectedProduct?.product_id)).then(res => {
        const productBenefits = res?.payload?.data?.product_benefits_summary_limits || {};
        const aggregationLevel = res?.payload?.data?.aggregation_level_code === "Group" ? "shared" : "shared";
        
        // Process benefits dynamically
        const processBenefits = (benefits) => {
          const result = {};
          Object.entries(benefits).forEach(([benefitType, limit]) => {
            const benefitKey = benefitType.toLowerCase();
            result[`${benefitKey}_limit`] = parseInt(limit).toLocaleString("US");
            result[`${benefitKey}_aggregation`] = aggregationLevel;
          });
          return result;
        };

        // Set form values for each category
        for (let category of CATEGORY_OPTIONS) {
          form.setFieldsValue({
            benefits_limits_and_aggregations: {
              [category]: processBenefits(productBenefits)
            }
          });
        }
      });
    }
  };

  // Initialize form and product selection
  useEffect(() => {
    // Check if product is already selected on initial render
    setTimeout(() => {
      const currentProduct = form.getFieldValue('product');
      
      // Check if this is U Care Premier Plus (case insensitive)
      const isUCarePremierPlus = 
        currentProduct && 
        (currentProduct.toLowerCase().includes('u care premier plus') || 
         currentProduct.toLowerCase().includes('ucare premier') || 
         currentProduct.toLowerCase().includes('premier plus'));
      
      if (isUCarePremierPlus) {
        setSelectedProductName(currentProduct);
      }
    }, 500); // Small delay to ensure form is fully initialized
  }, []); // Empty dependency array means this runs once on component mount

  return (
    <div style={{ padding: '24px', background: '#f5f5f5', minHeight: '100vh' }}>
      {messageContextHolder}
      <Card 
        style={{ 
          borderRadius: '16px',
          boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
          marginBottom: '24px',
          background: '#ffffff'
        }}
      >
        <div style={{ 
          display: 'flex', 
          alignItems: 'center', 
          marginBottom: '32px',
          borderBottom: '2px solid #f0f0f0',
          paddingBottom: '20px'
        }}>
          <Title level={4} style={{ margin: 0, color: '#005B60' }}> 
            {isRenewal ? "Renewal Quote Request" : "Request for Underwriter Quote"}
            {isRenewal && renewalAccountId && (
              <Tag color="#005b60" style={{ 
                marginLeft: '12px', 
                borderRadius: '6px',
                padding: '4px 12px',
                fontSize: '14px'
              }}>
                Account ID: {renewalAccountId}
              </Tag>
            )}
          </Title>
        </div>

        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
          initialValues={{
            region: "Kenya",
            funding_arrangement: "Insured",
            aggregation_level: "Non-Shared Limit",
          }}
        >
          <Card 
            title={<span style={{ 
              fontSize: '18px', 
              fontWeight: '600',
              color: '#005B60'
            }}>Client & Business Details</span>}
            style={{ 
              marginBottom: 32,
              borderRadius: '12px',
              boxShadow: '0 2px 12px rgba(0, 0, 0, 0.06)',
              border: '1px solid #e8e8e8'
            }}
            headStyle={{ 
              borderBottom: '2px solid #f0f0f0',
              padding: '20px 24px',
              background: '#fafafa'
            }}
            bodyStyle={{
              padding: '24px'
            }}
          >
            <Row gutter={[32, 24]}>
              <Col span={12}>
                <Form.Item
                  name="business_name"
                  label={<span style={{ fontWeight: '500' }}>Business Name</span>}
                  rules={[{ required: true }]}
                >
                  <Input 
                    placeholder="ABC Logistics Ltd" 
                    style={{ borderRadius: '6px' }}
                  />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  name="business_email"
                  label={<span style={{ fontWeight: '500' }}>Business Email</span>}
                  rules={[{ required: true }]}
                >
                  <Input 
                    placeholder="Enter the email" 
                    type="email"
                    style={{ borderRadius: '6px' }}
                  />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  name="registration_number"
                  label={<span style={{ fontWeight: '500' }}>Business Registration Number</span>}
                  rules={[{ required: true }]}
                >
                  <Input 
                    placeholder="BRN-0012345" 
                    style={{ borderRadius: '6px' }}
                  />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  name="pin_number"
                  label={<span style={{ fontWeight: '500' }}>PIN Number</span>}
                  rules={[{ required: true }]}
                >
                  <Input 
                    placeholder="P123456789A" 
                    style={{ borderRadius: '6px' }}
                  />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  name="nature_of_business"
                  label={<span style={{ fontWeight: '500' }}>Nature of Business</span>}
                  rules={[{ required: true }]}
                >
                  <Select 
                    placeholder="Select business type"
                    style={{ borderRadius: '6px' }}
                  >
                    {NATURE_OF_BUSINESS_OPTIONS.map((option) => (
                      <Select.Option key={option} value={option}>
                        {option}
                      </Select.Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  name="location_of_business"
                  label={<span style={{ fontWeight: '500' }}>Location of Business</span>}
                  rules={[{ required: true }]}
                >
                  <Select 
                    placeholder="Select a location" 
                    showSearch
                    style={{ borderRadius: '6px' }}
                  >
                    {LOCATION_OPTIONS.map((option) => (
                      <Select.Option key={option} value={option}>
                        {option}
                      </Select.Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  name="location_of_majority_insured_members"
                  label={<span style={{ fontWeight: '500' }}>Location of Majority Insured Members</span>}
                  rules={[{ required: true }]}
                >
                  <Select 
                    placeholder="Select a location" 
                    showSearch
                    style={{ borderRadius: '6px' }}
                  >
                    {LOCATION_OPTIONS.map((option) => (
                      <Select.Option key={option} value={option}>
                        {option}
                      </Select.Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  name="size_of_company"
                  label={<span style={{ fontWeight: '500' }}>Size of Company</span>}
                  rules={[{ required: true }]}
                >
                  <Input
                    placeholder="Enter the size"
                    type="number"
                    style={{ borderRadius: '6px' }}
                    onChange={(e) => {
                      const val = e?.target?.value;
                      setCompanySize(val ? Number(val) : null);
                      // Clear product selection when company size changes
                      form?.setFieldsValue?.({ product: undefined });
                      setSelectedProductName?.("");
                      setSelectedProductId?.(null);
                    }}
                  />
                </Form.Item>
              </Col>
            </Row>
          </Card>

          <Card 
            title={<span style={{ 
              fontSize: '18px', 
              fontWeight: '600',
              color: '#005B60'
            }}>Insurance Cover Details</span>}
            style={{ 
              marginBottom: 32,
              borderRadius: '12px',
              boxShadow: '0 2px 12px rgba(0, 0, 0, 0.06)',
              border: '1px solid #e8e8e8'
            }}
            headStyle={{ 
              borderBottom: '2px solid #f0f0f0',
              padding: '20px 24px',
              background: '#fafafa'
            }}
            bodyStyle={{
              padding: '24px'
            }}
          >
            <Row gutter={[32, 24]}>
              <Col span={12}>
                <Form.Item
                  name="funding_arrangement"
                  label={<span style={{ fontWeight: '500' }}>Funding Arrangement</span>}
                  rules={[{ required: true }]}
                >
                  <Select 
                    placeholder="Select funding arrangement"
                    style={{ borderRadius: '6px' }}
                  >
                    {FUNDING_ARRANGEMENT_OPTIONS.map((option) => (
                      <Select.Option key={option} value={option}>
                        {option}
                      </Select.Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  name="product"
                  label={<span style={{ fontWeight: '500' }}>Product</span>}
                  rules={[{ required: true }]}
                >
                  <Select
                    placeholder="Select Product"
                    onChange={handleProductChange}
                    defaultValue={selectedProductName}
                    showSearch
                    optionFilterProp="label"
                    filterOption={(input, option) =>
                      (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                    }
                    options={[
                      ...products?.filter(product => {
                        if (!companySize) return true;
                        const eligibility = Array.isArray(product?.product_eligibility)
                          ? product?.product_eligibility?.[0]
                          : product?.product_eligibility;
                        const min = eligibility?.minimum_group_size;
                        const max = eligibility?.maximum_group_size;
                        if (!min && !max) return true;
                        return (!min || companySize >= min) && (!max || companySize <= max);
                      })?.map(product => ({
                        key: product?.product_id,
                        value: product?.product_name,
                        label: product?.product_name,
                        funding_arrangement_code: product?.funding_arrangement_code,
                        aggregation_level_code: product?.aggregation_level_code
                      }))
                    
                    ]}
                  />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item 
                  name="cover_duration" 
                  label={<span style={{ fontWeight: '500' }}>Cover Duration(Months)</span>}
                  rules={[{ required: true }]}
                >
                  <Input 
                    placeholder="Enter the duration"
                    style={{ borderRadius: '6px' }}
                  />
                </Form.Item>
              </Col>
            </Row>
          </Card>

          <Card
            title={<span style={{ 
              fontSize: '18px', 
              fontWeight: '600',
              color: '#005B60'
            }}>Coverage & Benefits Selection</span>}
            style={{ 
              marginBottom: 32,
              borderRadius: '12px',
              boxShadow: '0 2px 12px rgba(0, 0, 0, 0.06)',
              border: '1px solid #e8e8e8'
            }}
            headStyle={{ 
              borderBottom: '2px solid #f0f0f0',
              padding: '20px 24px',
              background: '#fafafa'
            }}
            bodyStyle={{
              padding: '24px'
            }}
          >
            <Form.Item 
              name="scheme_category" 
              label={<span style={{ 
                fontWeight: '500',
                fontSize: '16px',
                color: '#262626'
              }}>Add Scheme Category</span>}
            >
              <Space direction="horizontal" wrap style={{ gap: '16px' }}>
                {CATEGORY_OPTIONS.map(option => (
                  <Checkbox
                    key={option}
                    value={option}
                    checked={selectedCategories.includes(option)}
                    onChange={handleCategoryChange}
                    style={{ 
                      marginRight: '16px',
                      fontSize: '15px'
                    }}
                  >
                    {option}
                  </Checkbox>
                ))}
                <Button
                  type="dashed"
                  onClick={handleAddCustomCategory}
                  icon={<PlusOutlined />}
                  style={{ 
                    borderRadius: '6px',
                    borderStyle: 'dashed',
                    borderColor: '#d9d9d9',
                    color: '#005B60'
                  }}
                >
                  Add Custom Category
                </Button>
              </Space>
            </Form.Item>

            {selectedCategories.map((category) => (
              <Card
                key={category}
                title={<span style={{ 
                  fontSize: '16px', 
                  fontWeight: '600',
                  color: '#005B60'
                }}>{category}</span>}
                style={{ 
                  marginBottom: 24,
                  borderRadius: '10px',
                  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.04)',
                  border: '1px solid #e8e8e8'
                }}
                headStyle={{ 
                  borderBottom: '1px solid #f0f0f0',
                  padding: '16px 20px',
                  background: '#fafafa'
                }}
                bodyStyle={{
                  padding: '20px'
                }}
                extra={
                  !CATEGORY_OPTIONS.includes(category) && (
                    <Button 
                      type="text" 
                      danger
                      onClick={() => {
                        setSelectedCategories(selectedCategories.filter(c => c !== category));
                      }}
                    >
                      Remove
                    </Button>
                  )
                }
              >
                <Row gutter={[32, 24]}>
                  <Col span={6}>
                    <Form.Item
                      name={[
                        "benefits_limits_and_aggregations",
                        category,
                        "inpatient_limit",
                      ]}
                      label={<span style={{ fontWeight: '500' }}>Inpatient Limit</span>}
                    >
                      {renderBenefitInput(category, "inpatient_limit", benefitOptions)}
                    </Form.Item>
                  </Col>
                  <Col span={6}>
                    <Form.Item
                      name={[
                        "benefits_limits_and_aggregations",
                        category,
                        "inpatient_aggregation",
                      ]}
                      label={<span style={{ fontWeight: '500' }}>Aggregation Level</span>}
                    >
                      <Select placeholder="Shared Limit">
                        <Select.Option value="shared">
                          Shared Limit
                        </Select.Option>
                        <Select.Option value="non_shared">
                          Non-Shared Limit
                        </Select.Option>
                      </Select>
                    </Form.Item>
                  </Col>
                  <Col span={6}>
                    <Form.Item
                      name={[
                        "benefits_limits_and_aggregations",
                        category,
                        "outpatient_limit",
                      ]}
                      label={<span style={{ fontWeight: '500' }}>Outpatient Limit</span>}
                    >
                      {renderBenefitInput(category, "outpatient_limit", benefitOptions)}
                    </Form.Item>
                  </Col>
                  <Col span={6}>
                    <Form.Item
                      name={[
                        "benefits_limits_and_aggregations",
                        category,
                        "outpatient_aggregation",
                      ]}
                      label={<span style={{ fontWeight: '500' }}>Aggregation Level</span>}
                    >
                      <Select placeholder="Shared Limit">
                        <Select.Option value="shared">
                          Shared Limit
                        </Select.Option>
                        <Select.Option value="non_shared">
                          Non-Shared Limit
                        </Select.Option>
                      </Select>
                    </Form.Item>
                  </Col>
                  <Col span={6}>
                    <Form.Item
                      name={[
                        "benefits_limits_and_aggregations",
                        category,
                        "optical_limit",
                      ]}
                      label={<span style={{ fontWeight: '500' }}>Optical Limit</span>}
                    >
                      {renderBenefitInput(category, "optical_limit", benefitOptions)}
                    </Form.Item>
                  </Col>
                  <Col span={6}>
                    <Form.Item
                      name={[
                        "benefits_limits_and_aggregations",
                        category,
                        "optical_aggregation",
                      ]}
                      label={<span style={{ fontWeight: '500' }}>Aggregation Level</span>}
                    >
                      <Select placeholder="Shared Limit">
                        <Select.Option value="shared">
                          Shared Limit
                        </Select.Option>
                        <Select.Option value="non_shared">
                          Non-Shared Limit
                        </Select.Option>
                      </Select>
                    </Form.Item>
                  </Col>
                  <Col span={6}>
                    <Form.Item
                      name={[
                        "benefits_limits_and_aggregations",
                        category,
                        "dental_limit",
                      ]}
                      label={<span style={{ fontWeight: '500' }}>Dental Limit</span>}
                    >
                      {renderBenefitInput(category, "dental_limit", benefitOptions)}
                    </Form.Item>
                  </Col>
                  <Col span={6}>
                    <Form.Item
                      name={[
                        "benefits_limits_and_aggregations",
                        category,
                        "dental_aggregation",
                      ]}
                      label={<span style={{ fontWeight: '500' }}>Aggregation Level</span>}
                    >
                      <Select placeholder="Shared Limit">
                        <Select.Option value="shared">
                          Shared Limit
                        </Select.Option>
                        <Select.Option value="non_shared">
                          Non-Shared Limit
                        </Select.Option>
                      </Select>
                    </Form.Item>
                  </Col>
                  <Col span={12}>
                    <Form.Item
                      name={[
                        "benefits_limits_and_aggregations",
                        category,
                        "copay",
                      ]}
                      label={<span style={{ fontWeight: '500' }}>Co Pay</span>}
                    >
                      <Input
                        prefix="KES"
                        placeholder="5,000,000"
                        onChange={(e) => {
                          const value = e.target.value.replace(/,/g, "");
                          if (!isNaN(value)) {
                            form.setFieldsValue({
                              benefits_limits_and_aggregations: {
                                [category]: {
                                  copay: Number(value).toLocaleString("US"),
                                },
                              },
                            });
                          }
                        }}
                      />
                    </Form.Item>
                  </Col>
                  <Col span={12}>
                    <Form.Item
                      name={[
                        "benefits_limits_and_aggregations",
                        category,
                        "service_provider_access",
                      ]}
                      label={<span style={{ fontWeight: '500' }}>Service Provider Access</span>}
                    >
                      <Select 
                        mode="multiple" 
                        placeholder="Select tiers"
                        defaultValue={SERVICE_PROVIDER_OPTIONS}
                      >
                        {SERVICE_PROVIDER_OPTIONS.map((option) => (
                          <Select.Option key={option} value={option}>
                            {option}
                          </Select.Option>
                        ))}
                      </Select>
                    </Form.Item>
                  </Col>
                </Row>
              </Card>
            ))}
          </Card>

          <Card
            title={<span style={{ 
              fontSize: '18px', 
              fontWeight: '600',
              color: '#005B60'
            }}>Additional Documents & Financial Information</span>}
            style={{ 
              marginBottom: 32,
              borderRadius: '12px',
              boxShadow: '0 2px 12px rgba(0, 0, 0, 0.06)',
              border: '1px solid #e8e8e8'
            }}
            headStyle={{ 
              borderBottom: '2px solid #f0f0f0',
              padding: '20px 24px',
              background: '#fafafa'
            }}
            bodyStyle={{
              padding: '24px'
            }}
          >
            <Row gutter={[32, 24]}>
              <Col span={12}>
                <Form.Item
                  name="claim_report"
                  label={<span style={{ 
                    fontWeight: '500',
                    fontSize: '16px',
                    color: '#262626'
                  }}>Previous Insurance Claim Report (if available)</span>}
                >
                  <Upload
                    {...uploadProps}
                    onChange={(info) => uploadProps.onChange(info, "claim_report")}
                  >
                    <Button
                      icon={<UploadOutlined />}
                      loading={fileUploadLoading}
                      style={{ 
                        borderRadius: '8px',
                        height: '40px',
                        padding: '0 24px',
                        background: '#f5f5f5',
                        borderColor: '#d9d9d9'
                      }}
                    >
                      Click to Upload
                    </Button>
                  </Upload>
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item 
                  name="client_budget" 
                  label={<span style={{ 
                    fontWeight: '500',
                    fontSize: '16px',
                    color: '#262626'
                  }}>Client Budget</span>}
                >
                  <Input
                    prefix="KES"
                    placeholder="5,000,000"
                    onChange={(e) => {
                      const value = e.target.value.replace(/,/g, "");
                      if (!isNaN(value)) {
                        form.setFieldsValue({
                          client_budget: Number(value).toLocaleString("US"),
                        });
                      }
                    }}
                  />
                </Form.Item>
              </Col>
            </Row>
          </Card>

          <Card
            title={<span style={{ 
              fontSize: '18px', 
              fontWeight: '600',
              color: '#005B60'
            }}>Historical Insights</span>}
            style={{ 
              marginBottom: 32,
              borderRadius: '12px',
              boxShadow: '0 2px 12px rgba(0, 0, 0, 0.06)',
              border: '1px solid #e8e8e8'
            }}
            headStyle={{ 
              borderBottom: '2px solid #f0f0f0',
              padding: '20px 24px',
              background: '#fafafa'
            }}
            bodyStyle={{
              padding: '24px'
            }}
          >
            <Row gutter={[32, 24]}>
              <Col span={12}>
                <Form.Item
                  name="prior_bidding_rounds"
                  label={<span style={{ fontWeight: '500' }}>Prior Bidding Rounds</span>}
                >
                  <Input 
                    placeholder="Enter number of prior bidding rounds"
                    style={{ borderRadius: '6px' }}
                  />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  name="previous_lives_covered"
                  label={<span style={{ fontWeight: '500' }}>Previous Lives Covered</span>}
                >
                  <Input 
                    placeholder="Enter previous number of lives covered"
                    style={{ borderRadius: '6px' }}
                  />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  name="claims_experience"
                  label={<span style={{ fontWeight: '500' }}>Claims Experience</span>}
                >
                  <TextArea
                    rows={4}
                    placeholder="Enter claims experience details..."
                    style={{ 
                      borderRadius: '8px',
                      resize: 'none'
                    }}
                  />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  name="current_provider"
                  label={<span style={{ fontWeight: '500' }}>Current Provider</span>}
                >
                  <Input 
                    placeholder="Enter current insurance provider"
                    style={{ borderRadius: '6px' }}
                  />
                </Form.Item>
              </Col>
            </Row>
          </Card>

          <Card
            title={<span style={{ 
              fontSize: '18px', 
              fontWeight: '600',
              color: '#005B60'
            }}>Sales Notes & Member Information</span>}
            style={{ 
              marginBottom: 32,
              borderRadius: '12px',
              boxShadow: '0 2px 12px rgba(0, 0, 0, 0.06)',
              border: '1px solid #e8e8e8'
            }}
            headStyle={{ 
              borderBottom: '2px solid #f0f0f0',
              padding: '20px 24px',
              background: '#fafafa'
            }}
            bodyStyle={{
              padding: '24px'
            }}
          >
            <Row gutter={[32, 24]}>
              <Col span={12}>
                <Form.Item 
                  name="notes" 
                  label={<span style={{ 
                    fontWeight: '500',
                    fontSize: '16px',
                    color: '#262626'
                  }}>Sales Notes</span>}
                >
                  <TextArea
                    rows={4}
                    placeholder="Enter any special requests or additional information..."
                    style={{ 
                      borderRadius: '8px',
                      resize: 'none'
                    }}
                  />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  name="member_information_template"
                  label={
                    <Space>
                      <span style={{ 
                        fontWeight: '500',
                        fontSize: '16px',
                        color: '#262626'
                      }}>Member Information Template</span>
                      <Typography.Link
                        href={MEMBER_TEMPLATE_URL}
                        target="_blank"
                        style={{ 
                          fontSize: '14px',
                          color: '#005B60'
                        }}
                      >
                        Download Template
                      </Typography.Link>
                    </Space>
                  }
                  extra={<span style={{ color: '#8c8c8c' }}>Please download and fill the template before uploading</span>}
                >
                  <Upload
                    {...uploadProps}
                    onChange={(info) => uploadProps.onChange(info, "member_information_template")}
                    accept=".xlsx,.xls,.csv"
                    showUploadList={false}
                  >
                    <Button
                      icon={<UploadOutlined />}
                      loading={fileUploadLoading}
                      style={{ 
                        borderRadius: '8px',
                        height: '40px',
                        padding: '0 24px',
                        background: '#f5f5f5',
                        borderColor: '#d9d9d9'
                      }}
                    >
                      Click to Upload
                    </Button>
                  </Upload>
                  {memberTemplateFile && (
                    <div
                      style={{
                        display: 'flex',
                        flexDirection: 'row',
                        flexWrap: 'wrap',
                        alignItems: 'center',
                        gap: 8,
                        marginLeft: 12,
                        maxWidth: '100%',
                      }}
                      className="member-template-file-actions"
                    >
                      <span
                        style={{
                          color: '#005B60',
                          fontWeight: 500,
                          marginRight: 8,
                          wordBreak: 'break-all',
                          // maxWidth: 220,
                          display: 'inline-block',
                          whiteSpace: 'pre-line',
                        }}
                      >
                        {memberTemplateFile.name}
                      </span>
                      <Button
                        size="small"
                        type="text"
                        danger
                        onClick={() => {
                          setMemberTemplateFile(null);
                          setMemberTableData([]);
                          setMemberTableHeaders([]);
                        }}
                        style={{ marginRight: 8, marginBottom: 4 }}
                      >
                        Remove
                      </Button>
                      <Button
                        style={{
                          borderRadius: 8,
                          background: '#005B60',
                          color: '#fff',
                          borderColor: '#005B60',
                          marginBottom: 4,
                        }}
                        onClick={() => setMemberPreviewModal(true)}
                      >
                        Preview Template
                      </Button>
                    </div>
                  )}
                </Form.Item>
              </Col>
            </Row>
          </Card>

          <Form.Item>
            <Space style={{ float: "right" }}>
              <Button 
                onClick={() => form.resetFields()}
                style={{ 
                  borderRadius: '8px',
                  padding: '0 32px',
                  height: '44px',
                  fontSize: '15px',
                  borderColor: '#d9d9d9',
                  background: '#f5f5f5'
                }}
              >
                Save Draft
              </Button>
              <Button 
                type="primary" 
                htmlType="submit" 
                loading={loading}
                style={{ 
                  borderRadius: '8px',
                  padding: '0 32px',
                  height: '44px',
                  fontSize: '15px',
                  background: '#005B60',
                  borderColor: '#005B60',
                  boxShadow: '0 2px 8px rgba(0, 91, 96, 0.15)'
                }}
              >
                Request for Quote
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Card>

      {/* Add Modal component */}
      <Modal
        title="Add Custom Category"
        open={isModalVisible}
        onOk={handleCustomCategorySubmit}
        onCancel={handleModalCancel}
        okText="Add Category"
        cancelText="Cancel"
        okButtonProps={{
          disabled: !customCategoryName.trim(),
          style: customCategoryName.trim() && {
            background: '#005B60',
            borderColor: '#005B60'
          }
        }}
      >
        <Form layout="vertical">
          <Form.Item
            label="Category Name"
            required
            rules={[{ required: true, message: 'Please enter category name' }]}
          >
            <Input
              placeholder="Enter custom category name"
              value={customCategoryName}
              onChange={(e) => setCustomCategoryName(e.target.value)}
              onPressEnter={handleCustomCategorySubmit}
            />
          </Form.Item>
        </Form>
      </Modal>

      {/* Member Template Preview Modal */}
      <Modal
        title="Preview Member Information Template"
        open={memberPreviewModal}
        onCancel={() => setMemberPreviewModal(false)}
        footer={null}
        width={1500}
      >
        <div style={{ border: '1px solid #eee', borderRadius: 8, padding: 16 }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                {memberTableHeaders.map((header, idx) => (
                  <th key={idx} style={{ border: '1px solid #ddd', padding: 8, background: '#fafafa' }}>{header}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {memberTableData.map((row, ridx) => (
                <tr key={ridx}>
                  {memberTableHeaders.map((header, cidx) => {
                    let value = row[normalize(header)];
                    if (normalize(header) === 'date of birth') {
                      value = excelSerialToDate(value);
                    }
                    return <td key={cidx} style={{ border: '1px solid #eee', padding: 8 }}>{value}</td>;
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Modal>
    </div>
  );
};

export default ManualQuoteRequest;

<style>{`
@media (max-width: 600px) {
  .member-template-file-actions {
    flex-direction: column !important;
    align-items: flex-start !important;
    gap: 4px !important;
  }
  .member-template-file-actions span {
    max-width: 100vw !important;
    margin-bottom: 4px !important;
  }
}`}</style>
